import { trText } from '@/i18n/plain';

/**
 * Parses either shape the API hands us and returns local midnight of that day.
 *
 * Two shapes are in play: date-only strings ("2026-08-25") from booking drafts and appointment
 * `date` fields, and full ISO timestamps ("2026-08-25T07:29:35.123Z") from Prisma `createdAt` /
 * `startAt` columns. Appending "T00:00:00" is only correct for the bare form — doing it to a
 * complete timestamp yields "…123ZT00:00:00", which is unparseable and renders as "Invalid Date".
 *
 * Normalising to local midnight afterwards keeps day-difference arithmetic honest: a UTC instant
 * parsed straight would sit at some local wall-clock time and could land a day either side.
 */
function toLocalMidnight(iso: string): Date | null {
  if (!iso) return null;
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  const parsed = new Date(dateOnly ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setHours(0, 0, 0, 0);
  return parsed;
}

export function formatFriendlyDate(iso: string): string {
  const target = toLocalMidnight(iso);
  if (!target) return '';

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diffDays = Math.round((target.getTime() - today.getTime()) / 86400000);
  if (diffDays === 0) return trText('Today', 'আজ');
  if (diffDays === 1) return trText('Tomorrow', 'আগামীকাল');
  if (diffDays === -1) return trText('Yesterday', 'গতকাল');
  return target.toLocaleDateString(trText('en-US', 'bn-BD'), { weekday: 'short', month: 'short', day: 'numeric' });
}

export function formatLongDate(iso: string): string {
  const target = toLocalMidnight(iso);
  if (!target) return '';
  return target.toLocaleDateString(trText('en-US', 'bn-BD'), { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

/**
 * Local YYYY-MM-DD for today + offset — for "what calendar date is it right now" (a default
 * reschedule date, "today" in a date picker, an availability query's `date` param).
 *
 * Never use `new Date().toISOString().slice(0, 10)` for this: `toISOString()` converts to UTC
 * first, so anywhere east of Greenwich in the evening (Bangladesh is UTC+6) it reports tomorrow's
 * date, and anywhere west of it in the early morning it reports yesterday's — a customer booking
 * at 1am local time would have their availability query, and a picker's "Today", land on the
 * wrong day.
 */
export function localDayISO(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const month = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}
